/** US (FDA) regulatory submission pathways — the dropdown options shown
 *  when a user picks US at dossier dispatch time. Grouped by product
 *  type so the picker can hide groups that don't apply (a device
 *  product never sees NDA/ANDA, etc.).
 *
 *  Edit a pathway, label, or default here — that's the only place. The
 *  ``value`` is the canonical key the backend's dossier router maps onto
 *  its dossier-template selection (eSTAR for the 510(k) family, eCTD for
 *  NDA/BLA/ANDA, etc.).
 *
 *  The per-country pathway files import the shared ``SubmissionOption`` +
 *  ``SubmissionGroup`` types from here:
 *    ``ca-submission-pathways.ts``  — Health Canada Class II/III/IV MDL.
 *    ``eu-submission-pathways.ts``  — EU MDR / IVDR device classes.
 */

import type { Product } from '@/features/product/types';

export interface SubmissionOption {
  value: string;
  label: string;
}

export interface SubmissionGroup {
  productType: Product['product_type'];
  label: string;
  options: SubmissionOption[];
}

export const US_SUBMISSION_PATHWAYS: SubmissionGroup[] = [
  {
    productType: 'device',
    label: 'Medical Device',
    options: [
      { value: '510k-exempt',           label: '510(k) Exempt — QMS & Technical Documentation package' },
      { value: '510k',                  label: '510(k) — Traditional Premarket Notification' },
      { value: '510k-special',          label: '510(k) Special — modification to your own cleared device (30-day clock)' },
      { value: '510k-abbreviated',      label: '510(k) Abbreviated — reliance on FDA-recognized consensus standards' },
      { value: 'denovo-no-clinical',    label: 'De Novo — without clinical data' },
      { value: 'denovo-clinical',       label: 'De Novo — with clinical data' },
      { value: 'pma',                   label: 'PMA — Premarket Approval' },
    ],
  },
  {
    productType: 'drug',
    label: 'Drug',
    options: [
      { value: 'nda',        label: 'NDA 505(b)(1) — full' },
      { value: 'nda-505b2',  label: 'NDA 505(b)(2) — hybrid' },
      { value: 'anda',       label: 'ANDA 505(j) — generic' },
    ],
  },
  {
    productType: 'biologic',
    label: 'Biologic',
    options: [
      { value: 'bla',        label: 'BLA 351(a) — full' },
      { value: 'biosimilar', label: 'Biosimilar — 351(k)' },
    ],
  },
];
